import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

import { graphql, useStaticQuery } from 'gatsby';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS } from '../../../constants';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { BREAKPOINTS } from '../../../styles/breakpoints';
import { AlternativeFund } from '../../../templates/AlternativeFund';
import { MutualFund } from '../../../templates/MutualFund';
import { ModuleBgColor } from '../../../templates/Page';
import { localize } from '../../../utils/sanity';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import CenteredCarousel from '../CenteredCarousel';
import FundCard from '../FundCard';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import VideoCard from '../VideoCard';
import * as styles from './FundsModule.module.scss';

export const FundsModuleFragment = graphql`
  fragment FundsModule on SanityPageFundsModule {
    title {
      ...LocaleStringFragment
    }
    text {
      ...LocaleTextFragment
    }
    textColor
    mobileBackgroundImage {
      ...SanityImage
    }
    desktopBackgroundImage {
      ...SanityImage
    }
    moduleLayout
    fundType
    fundsToUse
    alternativeFundsReferences {
      ...AlternativeFund
    }
    mutualFundsReferences {
      ...MutualFund
    }
    goldenVisaElegibleReferences {
      ...MutualFund
      ...AlternativeFund
    }
    fundsInfoToShow
  }
`;

export type FundsModuleProps = {
  title: string;
  text?: string;
  textColor: '#FFF' | '#333';
  mobileBackgroundImage: SanityImageType;
  desktopBackgroundImage: SanityImageType;
  moduleLayout: 'cardGrid' | 'carousel' | 'videos';
  className?: string;
} & (
  | ({
      fundType: 'mutualFunds';
      fundsInfoToShow: 'top5Assets' | 'metrics';
      alternativeFundsReferences?: never;
      goldenVisaElegibleReferences?: never;
    } & (
      | {
          fundsToUse: 'all';
          mutualFundsReferences?: never;
        }
      | {
          fundsToUse: 'chooseManually';
          mutualFundsReferences: Array<MutualFund>;
        }
    ))
  | ({
      fundType: 'alternativeFunds';
      fundsInfoToShow?: never;
      mutualFundsReferences?: never;
      goldenVisaElegibleReferences?: never;
    } & (
      | {
          fundsToUse: 'all';
          alternativeFundsReferences?: never;
        }
      | {
          fundsToUse: 'chooseManually';
          alternativeFundsReferences: Array<AlternativeFund>;
        }
    ))
  | ({
      fundType: 'goldenVisaElegible';
      fundsInfoToShow?: never;
      mutualFundsReferences?: never;
      alternativeFundsReferences?: never;
    } & (
      | {
          fundsToUse: 'all';
          goldenVisaElegibleReferences?: never;
        }
      | {
          fundsToUse: 'chooseManually';
          goldenVisaElegibleReferences: Array<AlternativeFund | MutualFund>;
        }
    ))
);

interface QueryData {
  allSanityAlternativeFund: {
    nodes: Array<AlternativeFund>;
  };
  allSanityMutualFund: {
    nodes: Array<MutualFund>;
  };
}

export function getModuleBgColor(props: FundsModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'colored';
}

function FundsModule(props: FundsModuleProps & CommonModuleProps): React.ReactElement {
  const queryData = useStaticQuery<QueryData>(graphql`
    {
      allSanityAlternativeFund(sort: { orderRank: ASC }) {
        nodes {
          ...AlternativeFund
        }
      }
      allSanityMutualFund: allSanitySecuritiesFund(sort: { orderRank: ASC }) {
        nodes {
          ...MutualFund
        }
      }
    }
  `);

  const {
    title,
    text,
    textColor,
    mobileBackgroundImage,
    desktopBackgroundImage,
    moduleLayout,
    fundType,
    fundsToUse,
    alternativeFundsReferences,
    mutualFundsReferences,
    goldenVisaElegibleReferences,
    fundsInfoToShow,
    className,
    previousModuleBgColor,
    moduleId,
  } = props;

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(queryData, langs);

  const { allSanityAlternativeFund, allSanityMutualFund } = localizedData;

  const containerRef = useRef<HTMLElement | null>(null);

  let fundsToShow: Array<AlternativeFund | MutualFund> = [];
  if (fundType === 'alternativeFunds') {
    fundsToShow =
      fundsToUse === 'chooseManually' ? alternativeFundsReferences : allSanityAlternativeFund.nodes;
  }
  if (fundType === 'mutualFunds') {
    fundsToShow =
      fundsToUse === 'chooseManually' ? mutualFundsReferences : allSanityMutualFund.nodes;
  }
  if (fundType === 'goldenVisaElegible') {
    fundsToShow =
      fundsToUse === 'chooseManually'
        ? goldenVisaElegibleReferences
        : [...allSanityAlternativeFund.nodes, ...allSanityMutualFund.nodes].filter(
            fund => !!fund.isGoldenVisaElegible,
          );
  }

  const fundsToShowWithVideo = fundsToShow.filter(fund => !!fund.videoUrl);

  const [fundsRendered, setFundsRendered] = useState(moduleLayout !== 'carousel');

  useGSAP(
    () => {
      if (!fundsRendered || !hasFundsToShow) {
        return; // avoid errors on console
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.fundsContainer}`,
          start: 'top 85%',
        } satisfies ScrollTrigger.Vars,
      });

      tl.set(`.${styles.fundCardWrapper}`, {
        transition: 'none',
        immediateRender: true,
      });

      tl.from(`.${styles.fundCardWrapper}`, {
        y: 100,
        opacity: 0,
        stagger: 0.2,
        duration: 0.6,
        clearProps: 'transition,transform',
      });
    },
    { scope: containerRef, dependencies: [fundsRendered] },
  );

  function renderFundCard(fund: MutualFund | AlternativeFund, isCarousel?: boolean) {
    return (
      <FundCard
        className={styles.fundCardWrapper}
        isCarouselCard={isCarousel}
        title={fund.name}
        text={fund.shortDescription}
        button={{
          text: t('fund_card.see_fund_link', 'Ver fundo'),
          linkType: 'internalLink',
          internalLink: {
            pageReference: { __typename: fund.__typename, slug: fund.slug },
          },
        }}
        fundsInfoToShow={fundsInfoToShow}
        metrics={fundType === 'mutualFunds' && 'metrics' in fund ? fund.metrics : undefined}
        investmentRisk={
          fundType === 'mutualFunds' && 'investmentRisk' in fund
            ? fund.investmentRisk?.investmentRisk
            : undefined
        }
        assetTypes={fundType === 'mutualFunds' && 'assetTypes' in fund ? fund.assetTypes : undefined}
        top5Holdings={
          fundType === 'mutualFunds' && 'top5Holdings' in fund ? fund.top5Holdings : undefined
        }
        top5Issuers={
          fundType === 'mutualFunds' && 'top5Issuers' in fund ? fund.top5Issuers : undefined
        }
      />
    );
  }

  const hasFundsToShow =
    (moduleLayout === 'videos' && fundsToShowWithVideo.length > 0) || fundsToShow.length > 0;

  return hasFundsToShow ? (
    <ModuleLayout
      id={moduleId}
      className={clsx(className, styles.container)}
      contentClassName={clsx(
        styles.contentContainer,
        moduleLayout === 'cardGrid' && styles.cardGridLayout,
        moduleLayout === 'carousel' && styles.carouselLayout,
        moduleLayout === 'videos' && styles.videosLayout,
      )}
      moduleRef={containerRef}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
      childrenClassName={styles.content}
      childrenWithoutHorizontalPadding
      theme={textColor !== '#FFF' ? 'light' : 'dark'}
    >
      <div className={styles.imageContainer}>
        <Image
          className={clsx(styles.image)}
          image={mobileBackgroundImage}
          dimensions={[
            [1000, 1800],
            [BREAKPOINTS.tabletHorizontal, desktopBackgroundImage, 2880, 1920],
          ]}
          cover
        />
        <div className={styles.overlay}></div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={clsx(styles.textContainer, textColor === '#FFF' && styles.whiteTextColor)}>
          <h2 className={styles.title}>{title}</h2>
          {text && <p className={styles.text}>{text}</p>}
        </div>
        <div className={styles.fundsContainer}>
          {moduleLayout === 'videos' &&
            fundsToShowWithVideo.length > 0 &&
            fundsToShowWithVideo.map((fund, i) => (
              <React.Fragment key={i}>
                <VideoCard
                  className={styles.fundCardWrapper}
                  videoUrl={fund.videoUrl!}
                  title={fund.name}
                  text={fund.shortDescription}
                  button={{
                    text: t('fund_card.see_fund_link', 'Ver fundo'),
                    linkType: 'internalLink',
                    internalLink: {
                      pageReference: { __typename: fund.__typename, slug: fund.slug },
                    },
                  }}
                />
              </React.Fragment>
            ))}

          {moduleLayout === 'carousel' && (
            <CenteredCarousel
              itemsIdealWidth={450}
              onInit={() => {
                setFundsRendered(true);
              }}
            >
              {fundsToShow.map((fund, i) => (
                <React.Fragment key={i}>{renderFundCard(fund, true)}</React.Fragment>
              ))}
            </CenteredCarousel>
          )}

          {moduleLayout === 'cardGrid' &&
            fundsToShow.map((fund, i) => (
              <React.Fragment key={i}>{renderFundCard(fund)}</React.Fragment>
            ))}
        </div>
      </div>
    </ModuleLayout>
  ) : (
    <></>
  );
}

export default FundsModule;
